import { FETCH_DEALER_SUCCESS } from 'constants/ActionTypes';
import { ADVICE_STATUS } from 'constants/ChecklistConstants';
import * as types from 'modules/checklists/actionTypes';
import type {
  CreateSuccessAction,
  DeleteChecklistSuccessAction,
  DuplicateChecklistSuccess,
  FetchChecklistsSelectSuccessAction,
  FetchChecklistSuccessAction,
  FetchDefaultChecklistSuccessAction,
  FetchSelectedChecklistsSuccess,
  SearchChecklistsSuccessAction,
  UpdateSuccessAction,
} from 'modules/checklists/types';
import type { DetailedChecklist } from 'modules/checklists/types/projections/detailed-checklist';
import type { Action } from 'modules/global/generic.types';
import { ASSIGN_ENTITY_FROM_GROUP_SUCCESS, FETCH_GROUP_SUCCESS } from 'modules/groups/actionTypes';
import { FETCH_INTERVENTION_SUCCESS, UPDATE_INTERVENTION_SUCCESS } from 'modules/interventions/actionTypes';
import type { FetchInterventionSuccessAction, UpdateInterventionSuccessAction } from 'modules/interventions/types/actions';
import { FETCH_MODEL_SUCCESS } from 'modules/selfServices/actionTypes';
import type { GetModelSuccessAction } from 'modules/selfServices/types';
import { createReducer } from 'utils/ReduxUtils';

export type ChecklistDataByIdReducer = Record<string, DetailedChecklist>;

const initialState: ChecklistDataByIdReducer = {};

const indexById = (checklists: DetailedChecklist[]): ChecklistDataByIdReducer =>
  checklists.reduce<ChecklistDataByIdReducer>((acc, checklist) => ({ ...acc, [checklist.id]: checklist }), {});

export const checklistFormatter = (checklist: DetailedChecklist): DetailedChecklist => ({
  ...checklist,
  items: checklist.items?.map(item => ({
    ...item,
    advices: item.advices?.map(advice => ({
      medias: [],
      ...advice,
    })),
  })),
});

const afterFetchChecklists = (
  state: ChecklistDataByIdReducer,
  {
    payload,
  }: SearchChecklistsSuccessAction | FetchChecklistsSelectSuccessAction | FetchSelectedChecklistsSuccess
): ChecklistDataByIdReducer => ({
  ...state,
  ...indexById(
    payload.content.map(checklist =>
      checklistFormatter({
        ...(state[checklist.id] ?? {}),
        ...checklist,
        items: checklist.items?.map(item => ({
          ...item,
          advices: item.advices?.map(advice => ({
            ...advice,
            status: ADVICE_STATUS.PENDING,
          })),
        })),
      }))
  ),
});

const afterFetchChecklist = (
  state: ChecklistDataByIdReducer,
  {
    payload: checklist,
    meta = {},
  }:
    | FetchChecklistSuccessAction
    | UpdateSuccessAction
    | CreateSuccessAction
    | DuplicateChecklistSuccess
    | FetchDefaultChecklistSuccessAction
): ChecklistDataByIdReducer => {
  const { source } = meta;
  if (!source || source === 'checklists') {
    return {
      ...state,
      [checklist.id]: checklistFormatter({
        ...(state[checklist.id] ?? {}),
        ...checklist,
      }),
    };
  }
  return state;
};

const afterDeleteChecklist = (
  state: ChecklistDataByIdReducer,
  { payload: checklistId }: DeleteChecklistSuccessAction
): ChecklistDataByIdReducer => {
  const { [checklistId]: _removed, ...newState } = state;
  return newState;
};

const setFromIntervention = (
  state: ChecklistDataByIdReducer,
  { payload: intervention }: FetchInterventionSuccessAction | UpdateInterventionSuccessAction
): ChecklistDataByIdReducer => ({
  ...state,
  ...indexById(
    (intervention?.selfServices ?? [])
      .flatMap(selfService => ('checklists' in selfService ? selfService.checklists : []))
      .map(checklist => checklistFormatter({ ...(state[checklist.id] ?? {}), ...checklist }))
  ),
});

const setFromModel = (
  state: ChecklistDataByIdReducer,
  { payload: model }: GetModelSuccessAction
): ChecklistDataByIdReducer => ({
  ...state,
  ...indexById(
    (model && 'checklists' in model ? model.checklists : []).map(checklist =>
      checklistFormatter({ ...(state[checklist.id] ?? {}), ...checklist }))
  ),
});

type ChecklistAssignments = Record<string, Record<string, DetailedChecklist[]>>;
type DealerConfigSuccessAction = {
  payload: {
    assignments?: { extras?: ChecklistAssignments; breakdownExtras?: ChecklistAssignments };
    projectionType?: string;
  };
};

const extractFromAssignments = (assignments?: ChecklistAssignments): DetailedChecklist[] =>
  Object.values(assignments ?? {}).flatMap(byModule => Object.values(byModule).flat());

const setFromDealerConfig = (
  state: ChecklistDataByIdReducer,
  { payload: entity }: DealerConfigSuccessAction
): ChecklistDataByIdReducer => {
  const checklists = [
    ...extractFromAssignments(entity.assignments?.extras),
    ...extractFromAssignments(entity.assignments?.breakdownExtras),
  ].filter(checklist => checklist?.id);

  if (!checklists.length) {
    return state;
  }

  return {
    ...state,
    ...indexById(
      checklists.map(checklist =>
        checklistFormatter({
          ...(state[checklist.id] ?? {}),
          ...checklist,
        }))
    ),
  };
};

const dealerConfigMatcher = ({ type, payload }: Action): boolean =>
  [FETCH_DEALER_SUCCESS, FETCH_GROUP_SUCCESS].includes(type) &&
  (payload as { projectionType?: string } | undefined)?.projectionType === 'config';

export default createReducer(
  initialState,
  {
    [types.SEARCH_CHECKLISTS_SUCCESS]: afterFetchChecklists,
    [types.FETCH_CHECKLISTS_SELECT_SUCCESS]: afterFetchChecklists,
    [types.FETCH_SELECTED_CHECKLISTS_SUCCESS]: afterFetchChecklists,
    [types.FETCH_SUCCESS]: afterFetchChecklist,
    [types.UPDATE_SUCCESS]: afterFetchChecklist,
    [types.CREATE_SUCCESS]: afterFetchChecklist,
    [types.DUPLICATE_CHECKLIST_SUCCESS]: afterFetchChecklist,
    [ASSIGN_ENTITY_FROM_GROUP_SUCCESS]: afterFetchChecklist,
    [types.FETCH_DEFAULT_CHECKLIST_SUCCESS]: afterFetchChecklist,
    [types.DELETE_CHECKLIST_SUCCESS]: afterDeleteChecklist,

    [FETCH_INTERVENTION_SUCCESS]: setFromIntervention,
    [UPDATE_INTERVENTION_SUCCESS]: setFromIntervention,

    [FETCH_MODEL_SUCCESS]: setFromModel,
  },
  [{ matcher: dealerConfigMatcher, reducer: setFromDealerConfig }]
);
